import { Box, Button, Container, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormAutoComplete from "./../../Controllers/AutoComplete";
import service from "../public/gifService";
import { useHistory } from "react-router";
import CustomBox from "../public/Components/CustomBox";
import { GifColors } from "../../theme";
import { useSelector } from "react-redux";
import { contactSelector } from "../../store/userSlice";
import { useLoading } from "../../providers/loadingProvider";

const useStyles = makeStyles((theme) => ({
  signupform: {
    backgroundColor: GifColors.lightBlue,
    borderRadius: 20,
    padding: 15,
    zIndex: 1,
  },
  autocomplete: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  applicantName: {
    fontWeight: 500,
    color: GifColors.generalBlack,
    fontSize: 17,
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
    "& > *": {
      marginTop: theme.spacing(2),
    },
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  bigDots: {
    position: "relative",
    zIndex: 0,
    width: "100%",
  },
}));

const defaultState = {
  institutions: [],
  researchAreas: [],
  programTypes: [],
  researchSubAreas: [],
  form: {},
};

const getTitleName = (titleCode) => {
  switch (titleCode) {
    case 100000000:
      return "Dr.";
    case 100000001:
      return "Prof. Dr.";
    case 100000002:
      return "Prof.";
    case 100000003:
      return "Ms.";
    case 100000004:
      return "Mr.";
    case 100000005:
      return "Mrs.";
    default:
      return "";
  }
};

const buildProposalData = (data, contact) => {
  return {
    proposal: {
      programType: data.programType,
    },
    titleCode: contact.title,
    firstname: contact.firstName,
    lastname: contact.lastName,
    Institution: data.institutionId,
    ResearchAreaNumber: data.researchAreaCode,
    ResearchSubAreaNumber: data.researchSubAreaCode,
    PrimaryEmail: contact.email,
    country: "Israel",
  };
};

export default function NewProposal() {
  const methods = useForm();
  const { handleSubmit, watch, setValue } = methods;
  const classes = useStyles();
  const [state, setState] = useState({ ...defaultState });
  const history = useHistory();
  const loading = useLoading();
  const contact = useSelector(contactSelector);
  const [creating, setCreating] = useState(false);

  const getInstitutions = useCallback(async () => {
    const institutions = (await service.getAllInstitutions()).map((i) => ({
      id: i.institutionId,
      label: i.name,
    }));
    setState((state) => ({ ...state, institutions }));
  }, []);

  const getReseachAreas = useCallback(async () => {
    const researchAreas = (await service.getResearchAreas()).map((t) => ({
      id: t.researchAreaCode,
      label: t.name,
      guid: t.researchAreaId,
    }));
    setState((state) => ({ ...state, researchAreas }));
  }, []);

  const getProgramTypes = useCallback(async () => {
    const programTypes = (await service.GetRegistrationPrograms()).map((t) => ({
      id: t.number,
      label: t.name,
    }));

    setState((state) => ({ ...state, programTypes }));
  }, []);

  const researchAreaCodeWatcher = watch("researchAreaCode");
  useEffect(() => {
    const getResearchSubAreas = async () => {
      const area = state.researchAreas.find(
        (ra) => ra.id === researchAreaCodeWatcher
      );
      if (!area) {
        setState((state) => ({ ...state, researchSubAreas: [] }));
        return;
      }

      const researchSubAreas = (
        await service.getResearchSubAreas({
          areaId: area.guid,
        })
      ).map((sa) => ({
        id: sa.researchSubAreaCode,
        label: sa.name,
        guid: sa.researchSubAreaId,
      }));
      setState((state) => ({ ...state, researchSubAreas }));
    };
    getResearchSubAreas();
    setValue("researchSubAreaCode", "");
  }, [researchAreaCodeWatcher, setValue, state.researchAreas]);

  useEffect(() => {
    const loadingId = loading.addLoading("...");
    Promise.all([getProgramTypes(), getInstitutions(), getReseachAreas()]).then(
      () => loading.removeLoading(loadingId)
    );
  }, [getInstitutions, getReseachAreas, getProgramTypes, loading]);

  const onSubmit = async (data) => {
    setCreating(true);
    try {
      const proposalData = buildProposalData(data, contact);
      await service.createProposal(proposalData);
      setCreating(false);
      history.push("/applicantsportal/generalinformation/overview");
    } catch (ex) {
      setCreating(false);
    }
  };

  return (
    <>
      <Box marginBottom={-55} position="relative" zIndex={10}>
        <CustomBox
          marginBottom={1}
          textAlign="center"
          fw={500}
          fs={17}
          color={GifColors.generalBlack}
        >
          CREATE NEW PROPOSAL
        </CustomBox>
        <Container
          component="main"
          maxWidth="md"
          className={classes.signupform}
        >
          <FormProvider {...methods}>
            <form
              className={classes.form}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Typography
                className={classes.applicantName}
              >{`Applicant: ${getTitleName(contact?.title)} ${
                contact?.firstName
              } ${contact?.lastName} `}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={12}>
                  <FormAutoComplete
                    name="programType"
                    label="Program Type"
                    options={state.programTypes}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  <FormAutoComplete
                    name="institutionId"
                    label="Institution"
                    options={state.institutions}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FormAutoComplete
                    name="researchAreaCode"
                    label="Research Area"
                    options={state.researchAreas}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FormAutoComplete
                    name="researchSubAreaCode"
                    label="Research Sub Area"
                    options={state.researchSubAreas}
                    required
                  />
                </Grid>
              </Grid>
              <Button
                disabled={creating}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {creating ? "Creating Proposal..." : "Create Proposal"}
              </Button>
            </form>
          </FormProvider>
        </Container>
      </Box>
      <img
        src="/images/gif-dots-large.svg"
        alt="dots"
        className={classes.bigDots}
      />
    </>
  );
}
